import { useCurrentUser } from "#/components/context/user/UserContext"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
} from "#/components/cmd-enrollment/shared"
import { Steps } from "#/components/cmd-enrollment/workflow/steps"
import React, { useEffect } from "react"
import {
  FieldNameHealthPlanId,
  FieldNameTradingPartner,
  UseConnectHealthPlan,
  useConnectHealthPlan,
  UseTradingPartners,
  useTradingPartners
} from "./hooks"
import { useUserDeductible } from "../../../../context/user/DeductibleContext"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, Typography } from "@carrotfertility/carotene-core"
import { FormTextField, Form, useFormContext } from "@carrotfertility/carotene-core-x"

export interface ProviderConnectYourInsuranceProps {
  viewName: typeof Steps[keyof typeof Steps]
  defaultTradingPartnerPk: string | null
  defaultHealthPlanId: string
  onUserUpdate: (healthPlanIdVal: string, tradingPartnerPkVal: string, tradingPartnerId: string) => any
  stepHeader: string
}

function SubmitButton({ onSubmit }: { onSubmit: UseConnectHealthPlan["onSubmit"] }): JSX.Element {
  const { handleSubmit } = useFormContext()
  return <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
}

function FormContent({
  defaultHealthPlanId,
  stepHeader,
  options,
  defaultTradingPartnerPk,
  checkIfProviderIsListed,
  providerListed,
  onSubmit
}: {
  defaultHealthPlanId: string
  stepHeader: string
  options: UseTradingPartners["options"]
  defaultTradingPartnerPk: string | null
  checkIfProviderIsListed: UseTradingPartners["checkIfProviderIsListed"]
  providerListed: boolean
  onSubmit: UseConnectHealthPlan["onSubmit"]
}): JSX.Element {
  const intl = useIntl()
  const { setValue } = useFormContext()

  useEffect(() => {
    setValue(FieldNameHealthPlanId, defaultHealthPlanId)
  }, [defaultHealthPlanId, setValue])

  return (
    <>
      <Box marginBottom={(theme) => theme.tokens.spacing.xxxl}>
        <BenefitEnrollmentFlowModalBackButton />
      </Box>
      <Typography
        id="step-heading"
        variant="h2"
        color={(theme) => theme.palette.text.primary}
        paddingBottom={(theme) => theme.tokens.spacing.xl}
      >
        {stepHeader}
      </Typography>
      <Box position="relative" zIndex={2}>
        <Typography
          id="provider-name-label"
          variant="body2"
          color={(theme) => theme.palette.text.primary}
          marginBottom={(theme) => theme.tokens.spacing.xs}
          display="block"
          fontWeight="bold"
          textTransform="uppercase"
        >
          <FormattedMessage defaultMessage="PROVIDER NAME" />
        </Typography>
        <BenefitEnrollmentModalFormSelect
          name={FieldNameTradingPartner}
          aria-labelledby="provider-name-label"
          defaultValue={defaultTradingPartnerPk}
          options={options.map((option) => ({ value: option.id, label: option.label }))}
          onChange={checkIfProviderIsListed}
        />
      </Box>
      <Box
        position="relative"
        zIndex={1}
        marginBottom={(theme) => (providerListed ? theme.tokens.spacing.xl : theme.tokens.spacing.xxxl)}
      >
        {providerListed && (
          <>
            <FormTextField
              name={FieldNameHealthPlanId}
              label={intl.formatMessage({ defaultMessage: "Member ID" })}
              defaultValue={defaultHealthPlanId ?? ""}
              registerOptions={{
                required: intl.formatMessage({ defaultMessage: "Required" }),
                pattern: {
                  value: /^[a-zA-Z0-9]+$/,
                  message: intl.formatMessage({ defaultMessage: "Only numbers and letters may be entered." })
                }
              }}
            />
          </>
        )}
      </Box>
      <Box height={(theme) => theme.spacing(theme.tokens.spacing.xxl)} />
      <SubmitButton onSubmit={onSubmit} />
    </>
  )
}

export function ProviderConnectYourInsurance({
  viewName,
  defaultTradingPartnerPk,
  defaultHealthPlanId,
  onUserUpdate,
  stepHeader
}: ProviderConnectYourInsuranceProps): JSX.Element {
  const { options, providerListed, availableTradingPartners, checkIfProviderIsListed } = useTradingPartners({
    defaultTradingPartnerPk
  })
  const { onSubmit } = useConnectHealthPlan({ viewName, onUserUpdate, availableTradingPartners })

  return (
    <Form onSubmit={onSubmit}>
      <FormContent
        defaultHealthPlanId={defaultHealthPlanId}
        stepHeader={stepHeader}
        options={options}
        defaultTradingPartnerPk={defaultTradingPartnerPk}
        checkIfProviderIsListed={checkIfProviderIsListed}
        providerListed={providerListed}
        onSubmit={onSubmit}
      />
    </Form>
  )
}

function ConnectHealthPlanStep({ isMember }: { isMember: boolean }): JSX.Element {
  const user = useCurrentUser()
  const { updateBenefitEnrollmentAnswers } = useUserDeductible()
  const intl = useIntl()

  const defaultTradingPartnerPk = isMember
    ? user.selectedTradingPartnerPk
    : user.partnerHealthPlanSelectedTradingPartnerPk
  const defaultHealthPlanId = isMember ? user.memberId : user.partnerHealthPlanMemberId

  const stepHeader = isMember
    ? intl.formatMessage({ defaultMessage: "Connect to your health insurance provider" })
    : intl.formatMessage({ defaultMessage: "Connect to their health insurance provider" })

  function onUserUpdate(healthPlanIdVal: string, tradingPartnerPkVal: string, tradingPartnerId: string): Promise<void> {
    return updateBenefitEnrollmentAnswers(
      isMember
        ? {
            selectedTradingPartnerId: tradingPartnerId,
            memberId: healthPlanIdVal,
            selectedTradingPartnerPk: tradingPartnerPkVal
          }
        : {
            partnerHealthPlanSelectedTradingPartnerId: tradingPartnerId,
            partnerHealthPlanMemberId: healthPlanIdVal,
            partnerHealthPlanSelectedTradingPartnerPk: tradingPartnerPkVal
          }
    )
  }

  return (
    <ProviderConnectYourInsurance
      defaultHealthPlanId={defaultHealthPlanId}
      onUserUpdate={onUserUpdate}
      defaultTradingPartnerPk={defaultTradingPartnerPk?.toString() || null}
      stepHeader={stepHeader}
      viewName={isMember ? Steps.MEMBER_HEALTH_PLAN_CONNECT : Steps.PARTNER_HEALTH_PLAN_CONNECT}
    />
  )
}

export function MemberConnectHealthPlanStep(): JSX.Element {
  return <ConnectHealthPlanStep isMember={true} />
}

export function PartnerConnectHealthPlanStep(): JSX.Element {
  return <ConnectHealthPlanStep isMember={false} />
}
